import { Button, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid';
import { JobTaskStatus } from '@vy/jobs-types';
import { defined } from '@vy/null';
import { isNullOrUndefined } from '@vy/utils';
import { FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useJobTasks } from '../../../../hooks/requests/use-job-tasks';
import { formatDate } from '../../../../utils/date';
import { VyTable } from '../../table/vy-table/VyTable';
import { VyDialog } from '../VyDialog';
import { formatTaskType } from '../VyNotificationCard/vyNotificationCardUtils';
import { getTaskNavigationUrl, mapJobTaskToNotification } from './VyTasksModalUtils';

type VyTasksModalProps = {
  open: boolean;
  onClose: () => void;
  jobId: string;
  jobTitle: string;
  taskStatusFilter?: JobTaskStatus;
};

export const VyTasksModal: FC<VyTasksModalProps> = ({ open, onClose, jobId, jobTitle, taskStatusFilter }) => {
  const loweredTaskStatusFilter = taskStatusFilter ? taskStatusFilter.toLowerCase() : undefined;
  const { data: tasks, isLoading } = useJobTasks({ jobId, enabled: open });
  const navigate = useNavigate();

  const tasksData = tasks?.map(mapJobTaskToNotification) ?? [];
  const filteredTasksData = defined(taskStatusFilter)
    ? tasksData.filter(({ status: taskStatus }) => taskStatus === taskStatusFilter)
    : tasksData;
  const taskCount = filteredTasksData.length;

  const columnDefinitions = useMemo<GridColDef[]>(
    () => [
      {
        field: 'type',
        headerName: 'Task Type',
        flex: 1,
        sortable: false,
        renderCell: (params) => (
          <span style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{formatTaskType(params.row.type)}</span>
        ),
      },
      {
        field: 'details',
        headerName: 'Details',
        flex: 2,
        sortable: false,
        renderCell: (params) => (
          <span style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>{params.value}</span>
        ),
      },
      {
        field: 'completedAt',
        headerName: 'Date',
        flex: 1,
        sortable: false,
        valueGetter: (_value: unknown, row) => {
          const date = row.completedAt ?? row.startedAt ?? row.createdAt;

          if (isNullOrUndefined(date)) {
            return 'N/A';
          }

          return date;
        },
        renderCell: (params) => (
          <span style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
            {formatDate(params.value, 'MMM DD, YY HH:mm:ss')}
          </span>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const navigationUrl = getTaskNavigationUrl(params.row);

          if (!navigationUrl) {
            return null;
          }

          return (
            <Button
              variant="outlined"
              size="small"
              onClick={() => navigate(navigationUrl)}
              sx={{
                fontSize: '12px',
                height: '30px',
                borderRadius: '4px',
                textTransform: 'none',
              }}
            >
              Go To
            </Button>
          );
        },
      },
    ],
    [],
  );

  return (
    <VyDialog
      open={open}
      onClose={onClose}
      label={`${jobTitle} ${loweredTaskStatusFilter ?? 'tasks summary'}`}
      labelDescription={
        <div>
          <strong>{taskCount}</strong> task{taskCount !== 1 ? 's' : ''} {loweredTaskStatusFilter}
        </div>
      }
      cancelButtonText="Close"
      maxWidth="md"
      fullWidth
      sx={{ zIndex: 99999 }}
    >
      <VyTable
        tableName="vy-tasks-modal"
        items={filteredTasksData}
        itemsCount={filteredTasksData.length}
        columns={columnDefinitions}
        loadingItems={isLoading}
        showPagination={false}
        useColumnVisibility={false}
        disableRowSelectionOnClick
        sortingMode="client"
        noRowsOverlay={() => <Typography sx={{ py: 2 }}>No tasks found</Typography>}
        getRowHeight={() => 'auto'}
        sx={{
          borderRadius: '10px',
          overflow: 'hidden',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#F5F6FA',
            borderTopLeftRadius: '10px',
            borderTopRightRadius: '10px',
            minHeight: '32px !important',
            maxHeight: '32px !important',
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#F5F6FA',
            '& .MuiDataGrid-columnHeaderTitle': {
              color: '#020f20',
              fontSize: '14px',
              fontWeight: 600,
            },
          },
          '& .MuiDataGrid-columnSeparator': {
            display: 'none',
          },
          '& .MuiDataGrid-row': {
            cursor: 'default !important',
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'flex-start',
            paddingTop: '16px',
            paddingBottom: '16px',
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
          },
        }}
      />
    </VyDialog>
  );
};
